import React, { PureComponent, Fragment } from 'react';
import { Marker, Circle, Polyline } from 'react-leaflet';
import L from 'leaflet';
import * as topojson from 'topojson-client';

import BasisMap from '~/core/scripts/map-components/BasisMap';
import OverlayLayer from '~/core/scripts/map-components/OverlayLayer';
import GeocoderMarker from '~/core/scripts/map-components/GeocoderMarker';
import Tooltip from '~/core/scripts/components/Tooltip';
import statesData from '~/assets/data/bundeslaender.topo.json';
import germanyData from '~/assets/data/bundeslaender_outline.topo.json';
import SichtungenLayer from './SichtungenLayer';
import TimelineControls from '../TimelineControls';
import DistanceGeocoder from '../DistanceGeocoder';
import Legend from '../Legend';
import { trackEvent } from '~/core/scripts/utils';
import { is375Query, is768Query, is960Query, is1280Query } from '~/core/scripts/mediaquery-helper';
import { loadData, getClosestArea } from './utils';

const isSmallScreen = !is768Query();
const isLargeScreen = is960Query();

let tickTimeout = null;

function getColorByStatus(status) {
  switch (status) {
    case 'Einzelwolf': return '#007369';
    case 'Paar': return '#74B03F';
    case 'Rudel': return '#FFDD45';
    default: return 'lightgray';
  };
}

function geoToTopo(data) {
  const key = Object.keys(data.objects)[0];
  return topojson.mesh(data, data.objects[key]);
}

function getBounds() {

  if (is960Query()) {
    return L.latLngBounds([55.5, 16], [46.3, 4.5]);
  }

  return L.latLngBounds([56, 16], [46.3, 4.5]);
}

function getMaxBounds() {

  if (is960Query()) {
    return L.latLngBounds([58.5, 26], [43.5, -7]);
  }

  return L.latLngBounds([58.5, 20], [43.5, -1]);
}

const maxBounds =  getMaxBounds();
const bounds = getBounds();
const wrapper = document.querySelector('#wolfmap');

class WolfMap extends PureComponent {

  state = {
    data: null,
    areas: null,
    currentYear: isSmallScreen ? 2017 : 2007,
    currentStep: isSmallScreen ? 1 : 0,
    isPlaying: isSmallScreen ? false : true,
    tooltipPos: { x: 0, y: 0 },
    tooltipData: null,
    tooltipVisible: false,
    geocoderLatLng: [],
    closestArea: null,
    value: null
  }

  minYear = 2000
  maxYear = 2017

  componentDidMount() {
    loadData().then(data => {
      this.setState({ ...data });
      document.body.className = `year-${this.state.currentYear}`;

      if (!isSmallScreen) {
        this.tick();
      }
    });
  }

  componentDidUpdate() {
    // hack for adding pointer events none
    const areaCircles = document.querySelectorAll('.area-circle');
    [].forEach.call(areaCircles, (el) => {
      if (el.getAttribute('fill-opacity') === '0') {
        el.style.pointerEvents = 'none';
      } else {
        el.style.pointerEvents = 'auto';
      }
    })
  }

  tick() {
    if (this.state.currentYear + 1 > this.maxYear || !this.state.isPlaying) {
      wrapper.classList.remove('is-playing');
      return this.setState({ isPlaying: false, currentStep: (this.state.currentStep + 1) % 2 });
    }

    wrapper.classList.add('is-playing');


    if (this.state.currentStep === 0) {
      this.setState({
        currentStep: (this.state.currentStep + 1) % 2,
      });
      tickTimeout = setTimeout(() => this.tick(), 1000);

      return false;
    }

    document.body.className = `year-${this.state.currentYear + 1}`;

    this.setState({
      currentYear: this.state.currentYear + 1,
      currentStep: (this.state.currentStep + 1) % 2,
    });

    tickTimeout = setTimeout(() => this.tick(), 200);
  }

  onMouseEnter = (layer) => {
    const { data } = this.state.areas.find(d => d.area === layer.target.options.areaname);

    this.setState({
      tooltipVisible: true,
      tooltipData: {
        data,
        firstYear:  data[0].year
      },
      tooltipPos: layer.containerPoint
    });
  }

  onMouseLeave = () => {
    this.setState({
      tooltipVisible: false
    });
  }

  onCloseTooltip = () => {
    this.setState({
      tooltipVisible: false
    });
  }

  renderAreas() {
    return this.state.areas
      .map(area => {
        const currentData = area.data.find(d => d.year === this.state.currentYear);
        const hasData = typeof currentData !== 'undefined';
        const sampleArea = area.data[0];
        const color = hasData ? getColorByStatus(currentData.Status) : '#555';
        const key = `${sampleArea.key}-${hasData ? 'active' : ''}`;
        const isExtraMarker = hasData && currentData['Extra-Marker'] !== '';
        const fillOpacity = isExtraMarker ? 0.15 : hasData ? 0.5 : 0;

        return (
          <Circle
            key={sampleArea.key}
            //icon={L.divIcon({ className: `area-marker ${currentData ? currentData.Status : ''} ${currentData ? 'is-active' : ''}`, iconAnchor: [8, 8], iconSize: [16, 16] })}
            center={sampleArea.LatLng}
            onMouseOver={this.onMouseEnter}
            radius={8000}
            onClick={this.onMouseEnter}
            onMouseOut={this.onMouseLeave}
            areaname={area.area}
            fillColor={color}
            color={color}
            fillOpacity={fillOpacity}
            opacity={hasData ? .9 : 0}
            weight={1}
            className={"area-circle"}
            zIndex={999}
          />
        );
      });
  }

  renderMovements() {
    return this.state.movements
      .filter(d => d.year === this.state.currentYear)
      .reduce((res, item) => {
        const circlePositions = item.steps[this.state.currentStep];

        circlePositions.forEach((pos, index) => {
          res.push({
            ...item,
            key: `${item.key}_${index}`,
            center: pos.center,
            type: pos.type,
            status: item.Status
          });
        });

        return res;
      }, [])
      .map(d => (
        <Marker
          key={d.key}
          icon={L.divIcon({ className: `wolf-marker ${d.type}`, iconAnchor: [2, 2], iconSize: [4, 4] })}
          position={d.center}
          zIndex={998}
        />
      ));
  }

  togglePlay = () => {
    this.setState({
      isPlaying: !this.state.isPlaying
    }, () => {
      trackEvent('toggle_play', this.state.isPlaying, 'class_B');

      if (this.state.isPlaying) {
        if (this.state.currentYear === this.maxYear) {
          document.body.className = `year-${this.minYear}`;

          return this.setState({ currentYear: this.minYear, currentStep: 0 }, () => this.tick());
        }

        return this.tick();
      }

      document.body.className = `year-${this.state.currentYear}`;
      wrapper.classList.remove('is-playing');
      clearTimeout(tickTimeout);
      this.setState({ currentStep: 1 });
    });
  }

  onChangeYear = (year) => {
    document.body.className = `year-${year}`;
    trackEvent('slider_drag', year, 'class_B');

    this.setState({
      currentYear: year
    });
  }

  onGeocode = (res) => {
    const latlng = res.response.view[0].result[0].location.displayPosition;
    const closestArea = getClosestArea(latlng, this.state.areas);

    document.body.className = 'year-2017';
    wrapper.classList.remove('is-playing');

    if (tickTimeout) {
      window.clearTimeout(tickTimeout);
    }

    trackEvent('geocoder__success', true, 'class_A')

    this.setState({
      isPlaying: false,
      currentYear: 2017,
      currentStep: 1,
      geocoderLatLng: [
        latlng.latitude + Math.random() / 10000,
        latlng.longitude + Math.random() / 10000
      ],
      closestArea
    });
  }

  onGeocoderClear = () => {
    this.setState({
      geocoderLatLng: [],
      closestArea: null,
      value: null
    });
  }

  setValue = (value) => {
    this.setState({ value });
  }

  render() {
    const { movements, areas } = this.state;
    const showDistanceLine = this.state.geocoderLatLng.length > 0 && this.state.currentYear === this.maxYear;

    return (
      <Fragment>
        <div className="map__wrapper">
          <BasisMap
            maxBounds={maxBounds}
            fitBounds={bounds}
            minZoom={4}
            maxZoom={12}
            showBasemap
            basemapUrl={"https://api.maptiler.com/maps/a0374530-3f31-4573-9641-48ccb3a44266/256/{z}/{x}/{y}.jpg?key=68ErAz6zqgdV9jEjhpuz"}
            showMapToggleButton
            showLabels
            labelPreset="germanStates"
            zoomDelta={1}
          >
            {areas && this.renderAreas()}
            {movements && this.renderMovements()}
            {areas && <OverlayLayer
              geojsonData={geoToTopo(statesData)}
              color="white"
              opacity={isSmallScreen ? 0.3 : 0.2}
              weight={isSmallScreen ? 1 : 0.5}
            />}
            {areas && <OverlayLayer
              geojsonData={geoToTopo(germanyData)}
              color={"white"}
              opacity={isSmallScreen ? 0.4 : 0.3}
              weight={1}
            />}
            {(areas && (this.state.currentYear === this.maxYear)) && <SichtungenLayer />}
            {showDistanceLine && (
              <Polyline
                className="distance-line"
                weight={1}
                color="white"
                opacity={0.75}
                positions={[this.state.geocoderLatLng, this.state.closestArea.area.LatLng]}
              />
            )}
            <GeocoderMarker
              position={this.state.geocoderLatLng}
            />
          </BasisMap>
          <TimelineControls
            minYear={this.minYear}
            maxYear={this.maxYear}
            currentYear={this.state.currentYear}
            isPlaying={this.state.isPlaying}
            togglePlay={this.togglePlay}
            onChangeYear={this.onChangeYear}
            tipProps={"this.props.currentYear"}
            tipFormatter={"percentFormatter"}
          />
          <Tooltip
            isVisible={this.state.tooltipVisible}
            pos={this.state.tooltipPos}
            data={this.state.tooltipData}
            onClose={this.onCloseTooltip}
            currentYear={this.state.currentYear}
          />
          <Legend
            areas={this.state.areas}
            currentYear={this.state.currentYear}
          />
        </div>
        <DistanceGeocoder
          onSelect={this.onGeocode}
          onClear={this.onGeocoderClear}
          area={this.state.closestArea}
          value={this.state.value}
          setValue={this.setValue}
          areas={areas}
          currentYear={this.state.currentYear}
        />
      </Fragment>
    );
  }
}

export default WolfMap;
